import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiBaseUrl } from "../utils/Constants";

const authHeaders = () => ({
    Authorization: localStorage.getItem("token"),
    "Content-Type": "application/json",
});

// ── Trigger clinical AI assessment ────────────────────────────────────────────
export const runClinicalAssessment = createAsyncThunk(
    "clinicalReview/run",
    async ({ projectId, patientId, projectChatId, serviceType }) => {
        try {
            const response = await axios.post(
                ApiBaseUrl + "/v1/user/get-pdf-answer",
                {
                    projectId,
                    patientId: patientId || "",
                    projectChatId: projectChatId || "all",
                    serviceType: serviceType || "",
                    userQuery:
                        "Generate a complete clinical assessment and treatment plan for this patient based on their intake answers and any uploaded pathology results.",
                },
                { headers: authHeaders() }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }
);

// ── Submit clinician review decision ─────────────────────────────────────────
export const submitClinicalReview = createAsyncThunk(
    "clinicalReview/submit",
    async ({ reviewId, action, editedOutput, clinicianNote }) => {
        try {
            const response = await axios.post(
                ApiBaseUrl + "/v1/clinical/review",
                { reviewId, action, editedOutput, clinicianNote },
                { headers: authHeaders() }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }
);

// ── Get pending reviews ───────────────────────────────────────────────────────
export const getPendingReviews = createAsyncThunk(
    "clinicalReview/getPending",
    async ({ projectId } = {}) => {
        try {
            const params = projectId ? { projectId } : {};
            const response = await axios.get(
                ApiBaseUrl + "/v1/clinical/reviews/pending",
                { headers: authHeaders(), params }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }
);

// ── Get single review by ID ───────────────────────────────────────────────────
export const getClinicalReviewById = createAsyncThunk(
    "clinicalReview/getById",
    async (reviewId) => {
        try {
            const response = await axios.get(
                ApiBaseUrl + "/v1/clinical/review/" + reviewId,
                { headers: authHeaders() }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }
);

// ── Get all reviews for a project ────────────────────────────────────────────
export const getReviewsByProject = createAsyncThunk(
    "clinicalReview/getByProject",
    async ({ projectId, patientId }) => {
        try {
            let url = ApiBaseUrl + "/v1/clinical/reviews/project/" + projectId;
            if (patientId) url += "?patientId=" + patientId;
            const response = await axios.get(url, { headers: authHeaders() });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }
);

// ── Get audit trail ───────────────────────────────────────────────────────────
export const getAuditTrail = createAsyncThunk(
    "clinicalReview/getAudit",
    async (projectId) => {
        try {
            const response = await axios.get(
                ApiBaseUrl + "/v1/clinical/audit/" + projectId,
                { headers: authHeaders() }
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }
);

// ── Slice ─────────────────────────────────────────────────────────────────────
const clinicalReviewSlice = createSlice({
    name: "clinicalReview",
    initialState: {
        isRunning: false,
        isSubmitting: false,
        isLoading: false,
        currentReview: null,      // the active ClinicalReview object from DB
        clinicalOutput: null,     // the parsed AI output (6 sections)
        reviewStatus: null,       // 'pending' | 'approved' | 'rejected'
        nextSteps: null,          // post-action guidance from backend
        pendingReviews: [],
        projectReviews: [],
        auditLogs: [],
        error: null,
    },
    reducers: {
        clearClinicalReview: (state) => {
            state.currentReview = null;
            state.clinicalOutput = null;
            state.reviewStatus = null;
            state.nextSteps = null;
            state.error = null;
        },
        setCurrentReview: (state, action) => {
            state.currentReview = action.payload;
            state.clinicalOutput = action.payload?.clinicianEdits || action.payload?.aiOutput || null;
            state.reviewStatus = action.payload?.status || null;
        },
    },
    extraReducers: (builder) => {
        // runClinicalAssessment
        builder
            .addCase(runClinicalAssessment.pending, (state) => {
                state.isRunning = true;
                state.error = null;
                state.currentReview = null;
                state.clinicalOutput = null;
            })
            .addCase(runClinicalAssessment.fulfilled, (state, action) => {
                state.isRunning = false;
                if (action.payload?.isClinical) {
                    state.currentReview = action.payload.data;
                    state.clinicalOutput = action.payload.clinicalOutput;
                    state.reviewStatus = "pending";
                }
            })
            .addCase(runClinicalAssessment.rejected, (state, action) => {
                state.isRunning = false;
                state.error = action.error.message;
            });

        // submitClinicalReview
        builder
            .addCase(submitClinicalReview.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(submitClinicalReview.fulfilled, (state, action) => {
                state.isSubmitting = false;
                if (action.payload?.data) {
                    state.currentReview = action.payload.data;
                    state.reviewStatus = action.payload.data.status;
                    state.clinicalOutput =
                        action.payload.data.clinicianEdits || action.payload.data.aiOutput;
                }
                // Store next-steps guidance returned by backend
                if (action.payload?.nextSteps) {
                    state.nextSteps = action.payload.nextSteps;
                }
            })
            .addCase(submitClinicalReview.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.error.message;
            });

        // getPendingReviews
        builder
            .addCase(getPendingReviews.fulfilled, (state, action) => {
                state.pendingReviews = action.payload?.data || [];
            });

        // getClinicalReviewById
        builder
            .addCase(getClinicalReviewById.pending, (state) => { state.isLoading = true; })
            .addCase(getClinicalReviewById.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload?.data) {
                    state.currentReview = action.payload.data;
                    state.clinicalOutput =
                        action.payload.data.clinicianEdits || action.payload.data.aiOutput;
                    state.reviewStatus = action.payload.data.status;
                }
            })
            .addCase(getClinicalReviewById.rejected, (state) => { state.isLoading = false; });

        // getReviewsByProject
        builder
            .addCase(getReviewsByProject.fulfilled, (state, action) => {
                const reviews = action.payload?.data || [];
                state.projectReviews = reviews;

                // Restore the most recent review into active state so the UI
                // shows the assessment after a page refresh.
                // Priority: pending first (needs clinician action), then most recent.
                if (reviews.length > 0) {
                    const pending = reviews.find(r => r.status === 'pending');
                    const mostRecent = pending || reviews[0]; // already sorted desc by backend
                    state.currentReview = mostRecent;
                    state.clinicalOutput = mostRecent.clinicianEdits || mostRecent.aiOutput || null;
                    state.reviewStatus = mostRecent.status;
                    // Restore nextSteps for approved/rejected reviews
                    if (mostRecent.status === 'approved') {
                        state.nextSteps = {
                            action: 'approved',
                            message: 'Treatment plan approved and locked. Patient has been notified.',
                            canRunNewAssessment: false,
                            showScript: true,
                            showFollowUp: true,
                            clinicalNotes: (mostRecent.clinicianEdits || mostRecent.aiOutput)
                                ?.actionChecklist?.clinicalNotes || ''
                        };
                    } else if (mostRecent.status === 'rejected') {
                        state.nextSteps = {
                            action: 'rejected',
                            message: 'Assessment rejected. You can update the intake form and run a new assessment.',
                            canRunNewAssessment: true,
                            showScript: false,
                            showFollowUp: false
                        };
                    }
                }
            });

        // getAuditTrail
        builder
            .addCase(getAuditTrail.fulfilled, (state, action) => {
                state.auditLogs = action.payload?.data || [];
            });
    },
});

export const { clearClinicalReview, setCurrentReview } = clinicalReviewSlice.actions;
export default clinicalReviewSlice.reducer;